import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Login.css';
import { DSIcon, DSInput, DSButton } from '../design-system/primitives';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setError('No reset token provided');
      return;
    }

    // Validate the token
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/validate-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setError('This reset link is invalid or has expired.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'This reset link is invalid or has expired.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/logo.png" alt="Capital Pinball" className="login-logo" />
          </div>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading">Validating reset link...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/logo.png" alt="Capital Pinball" className="login-logo" />
        </div>

        {success ? (
          <div className="login-form">
            <div style={{ textAlign: 'center' }}>
              <div style={{
                backgroundColor: 'var(--ds-color-statusSuccessBg)',
                border: '1px solid var(--ds-color-statusSuccessText)',
                padding: '20px',
                borderRadius: '5px',
                marginBottom: '20px',
                color: 'var(--ds-color-statusSuccessText)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}><DSIcon name="checkCircle" /></div>
                <strong>Password Reset Successfully!</strong>
                <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
                  Your password has been changed. You can now log in with your new password.
                </p>
              </div>
              
              <DSButton tone="primary" appearance="solid" className="login-button" onClick={() => navigate('/login')}>
                Go to Login
              </DSButton>
            </div>
          </div>
        ) : !tokenValid ? (
          <div className="login-form">
            <h2 style={{ color: 'var(--ds-color-textDefault)', marginBottom: '10px', fontSize: '20px', textAlign: 'center' }}>
              Reset Password
            </h2>
            <div style={{
              backgroundColor: 'var(--ds-color-statusErrorBg)',
              border: '1px solid var(--ds-color-statusErrorText)',
              padding: '20px',
              borderRadius: '5px',
              marginBottom: '20px',
              color: 'var(--ds-color-statusErrorText)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}><DSIcon name="warning" /></div>
              <strong>Invalid Reset Link</strong>
              <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
                {error || 'This password reset link is invalid or has expired.'}
              </p>
            </div>
            <Link 
              to="/forgot-password" 
              className="button button-primary login-button"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Request New Reset Link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <h2 style={{ color: 'var(--ds-color-textDefault)', marginBottom: '10px', fontSize: '20px', textAlign: 'center' }}>
              Reset Password
            </h2>
            
            {userEmail && (
              <p style={{ fontSize: '14px', color: 'var(--ds-color-textMuted)', textAlign: 'center', marginBottom: '20px' }}>
                Create a new password for <strong>{userEmail}</strong>
              </p>
            )}

            {error && <div className="alert alert-error">{error}</div>}

            <DSInput label="New Password"
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                minLength="8"
                disabled={isLoading}
                placeholder="At least 8 characters" />

            <DSInput label="Confirm New Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength="8"
                disabled={isLoading}
                placeholder="Re-enter your new password" />

            <div style={{
              backgroundColor: 'var(--ds-color-statusInfoBg)',
              padding: '12px',
              borderRadius: '5px',
              marginBottom: '20px',
              fontSize: '13px',
              color: 'var(--ds-color-statusInfoText)'
            }}>
              <strong>Password Tips:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Use at least 8 characters</li>
                <li>Mix letters, numbers, and symbols</li>
                <li>Avoid common words</li>
              </ul>
            </div>

            <DSButton tone="primary" appearance="solid" className="login-button" type="submit" disabled={isLoading}>
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </DSButton>
          </form>
        )}

        <div className="login-footer">
          <p>
            Remember your password? <Link to="/login">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
